import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useAuth } from "../../../../../../features/auth/AuthContext";
import { updateGlobalState } from "../../../../../../state/global/slice.tsx";
import { changeScrollPopup } from "../../../../../../utils/utlis.js";
import Loader from "../../teacher/course/Modals/Loader.jsx";
import { SubjectIcon } from "../Icons/Icons.jsx";
import { updateStudentSubjects } from "../profile.api.js";
import SubjectSelector from "./SubjectSelector.jsx";
import useSubjectSelection from "./useSubjectSelection.js";

/**
 * Shown to students who already have a profile but no subjects on it.
 * Single-step, single submit — it deliberately does not touch the
 * "student_formState" localStorage draft used by the onboarding wizard.
 */
const SubjectOnlyPopup = ({ onClose, initialSubjects = [] }) => {
  const { token } = useAuth();
  const dispatch = useDispatch();
  const globalState = useSelector((state) => state.globalState);
  const [saving, setSaving] = useState(false);

  const {
    subjects,
    subjectInput,
    setSubjectInput,
    suggestions,
    error,
    validating,
    addSubject,
    removeSubject,
    validate,
  } = useSubjectSelection(token, initialSubjects);

  // Lock the background scroll for as long as the popup is mounted.
  useEffect(() => {
    changeScrollPopup("hidden", "studentDashboar-popup-container");
    return () => changeScrollPopup("auto", "studentDashboar-popup-container");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validate();
    if (!isValid) return;

    setSaving(true);
    try {
      const data = await updateStudentSubjects(subjects, token);

      if (data?.status && data.status !== 200) {
        toast.dismiss();
        toast.error(data?.message || "Something went wrong!", {
          position: "top-center",
        });
        return;
      }

      // Keep the cached profile in sync so the popup does not reappear on the
      // next render before the dashboard refetches.
      dispatch(
        updateGlobalState({
          key: "student_details",
          value: {
            ...(globalState?.student_details ?? {}),
            data: {
              ...(globalState?.student_details?.data ?? {}),
              subject: subjects,
              subjects,
            },
          },
        }),
      );

      toast.dismiss();
      toast.success(data?.message || "Subjects saved successfully.", {
        position: "top-center",
      });
      onClose?.();
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to save your subjects. Please try again.", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  const busy = validating || saving;

  return (
    <div className="studentDashboar-popup-section relative">
      <div className="studentDashboar-popup-wrapper">
        <div className="studentDashboar-popup-container">
          <div className="dashboard-popup-form-wrapper">
            <div className="dashboard-popup-teacher-profile-details-folder">
              {busy && (
                <Loader
                  text={
                    saving
                      ? "Saving your subjects..."
                      : "Validating your subjects..."
                  }
                />
              )}
              <div className="dashboard-popup-teacher-profile-details-title">
                <div className="dashboard-popup-teacher-profile-details-image ml-1">
                  <SubjectIcon />
                </div>
                <h2>Subjects</h2>
              </div>
              <div className="dashboard-popup-teacher-profile-details-form">
                <SubjectSelector
                  subjects={subjects}
                  onAdd={addSubject}
                  onRemove={removeSubject}
                  suggestions={suggestions}
                  inputValue={subjectInput}
                  onInputChange={setSubjectInput}
                  error={error}
                />
              </div>
              <div className="form-buttons justify-center">
                <button
                  onClick={handleSubmit}
                  className="update-profile-image finish-button"
                  disabled={busy || subjects.length === 0}
                >
                  {saving ? <div className="loading-spinner"></div> : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectOnlyPopup;
